<template>
  <ContentfulHeroSections :item="item" :toc-info="tocInfo" />

  <ArticleTocLayout
    v-if="item.fields.contentSection && showTocValue"
    id="toc-container"
    :key="`toc-${item.sys.id}-${item.sys.updatedAt}`"
    :mobile-title="$textDictionary['toc.mobileTitle'] ?? 'Table of content'"
    :aside-aria-label="
      $textDictionary['toc.asideAriaLabel'] ?? 'Table of content'
    "
    :nav-heading="$textDictionary['toc.navHeading'] ?? 'Content of page'"
    :open-toc-btn-action-label="
      $textDictionary['toc.openTocBtnActionLabel'] ?? 'open'
    "
    :prev-btn-label="$textDictionary['toc.prevBtnLabel'] ?? 'previous'"
    :next-btn-label="$textDictionary['toc.nextBtnLabel'] ?? 'next'"
    :toc-skip-btn-text="
      $textDictionary['toc.tocSkipBtnText'] ?? 'Skip Table of content'
    "
  >
    <LibGroupedSections :entries="item.fields.contentSection" />
  </ArticleTocLayout>
  <LibGroupedSections
    v-else-if="item.fields.contentSection"
    :entries="item.fields.contentSection"
  />

  <LibGroupedSections
    v-if="item.fields.callToAction"
    :entries="[item.fields.callToAction]"
    :as-section="true"
  />
</template>

<script setup lang="ts">
import type { IContentPage, LOCALE_CODE } from '~/lib/ContentfulService';
import { ArticleTocLayout } from '@hypercodestudio/basler-components';
import type { TOCInfo } from '~/types/TocType';
import { mapToFormattedDate } from '~/utils/mapper/mapToFormattedDate';
import { useHostname } from '~/composables/useHostname';

const { $textDictionary, $locale } = useNuxtApp();

const PAGETYPE_TO_ARTICLETYPE: Record<string, string> = {
  'Knowledge Article': 'TechArticle',
  Tutorial: 'TechArticle',
  Product: 'WebPage',
  News: 'NewsArticle',
  'Use Case': 'TechArticle',
  'Customer Story	': 'WebPage',
  Miscellaneous: 'WebPage'
};

const readingTime = ref(1);

interface Props {
  item: IContentPage;
}

const props = defineProps<Props>();

const showTocValue = toRef(() => props.item?.fields?.showToc);

const tocInfo = computed<TOCInfo>(() => {
  return {
    showToc: showTocValue.value,
    centeredText: !showTocValue.value,
    date: props.item.fields?.date
      ? mapToFormattedDate(
          props.item.fields?.date.toString(),
          $locale.value as LOCALE_CODE
        )
      : undefined,
    readingTime: readingTime.value
  };
});

// Helper function to wait for an element to be available in the DOM
const waitForElement = async (
  elementId: string,
  maxRetries = 10,
  delayMs = 100
): Promise<HTMLElement | null> => {
  for (let i = 0; i < maxRetries; i++) {
    await nextTick();
    const element = document.getElementById(elementId);
    if (element) {
      return element;
    }
    // Wait a bit before retrying to allow for re-renders
    if (i < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
};

// Split reading time calculation from TOC rendering to avoid blocking
const calculateReadingTime = async () => {
  if (!tocInfo.value?.showToc) {
    return;
  }

  const { data: rt } = await useFetch<number>('/api/articles/getReadingTime', {
    query: {
      pageId: props.item.sys.id,
      lastUpdate: props.item.sys.updatedAt,
      locale: $locale.value
    }
  });

  if (rt.value) {
    readingTime.value = rt.value;
  } else {
    // Wait for DOM to be fully rendered with retry mechanism
    // This handles race conditions with parent component re-renders
    const tocContainer = await waitForElement('toc-container');

    if (tocContainer) {
      const text = tocContainer.innerText;
      const images = tocContainer.getElementsByTagName('img').length ?? 0;
      const videos =
        tocContainer.getElementsByTagName('.media-block').length ?? 0;
      const words = text ? text.split(/\s+/).length : 0;

      const { data: rt } = await useFetch<number>(
        '/api/articles/setReadingTime',
        {
          query: {
            pageId: props.item.sys.id,
            lastUpdate: props.item.sys.updatedAt,
            images: images,
            videos: videos,
            words: words,
            locale: $locale.value
          }
        }
      );

      if (rt.value) {
        readingTime.value = rt.value;
      }
    }
  }
};

onMounted(() => {
  // Calculate reading time asynchronously without blocking rendering
  calculateReadingTime();
});

// Recalculate reading time when the item changes (on navigation)
watch(
  () => props.item.sys.id,
  () => {
    calculateReadingTime();
  }
);

const { $globalPageSettings } = useNuxtApp();
const locale = useLocale();
const serverUrlOrigin = useHostname();
const url = computed(() =>
  buildUrlString(locale.value, undefined, undefined, undefined, serverUrlOrigin)
);

const schema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': props.item.fields.format
    ? PAGETYPE_TO_ARTICLETYPE[props.item.fields.format]
    : 'WebPage',
  ...(props.item.fields.date && {
    datePublished: new Date(props.item.fields.date).toISOString(),
    dateModified: new Date(props.item.fields.date).toISOString()
  }),
  headline: props.item.fields.teaserTitle ?? undefined,
  description: props.item.fields.teaserText ?? undefined,
  image:
    props.item.fields.teaserImage?.fields?.image?.fields?.file?.url ??
    undefined,
  author: [
    {
      '@type': 'Organization',
      name: $globalPageSettings.value?.companyName ?? undefined,
      url: url.value,
      logo: $globalPageSettings.value?.companyLogo?.url ?? undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: $globalPageSettings.value?.companyStreet ?? undefined,
        postalCode: $globalPageSettings.value?.companyPostalCode ?? undefined,
        addressLocality: $globalPageSettings.value?.companyCity ?? undefined,
        addressRegion: $globalPageSettings.value?.companyRegion ?? undefined,
        addressCountry:
          locale.value?.split('-').at(-1)?.toUpperCase() ?? undefined
      }
    }
  ],
  isAccessibleForFree: true
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema.value),
      tagPriority: -1 // Optional: Sicherstellen, dass es zuletzt eingefügt wird
    }
  ]
});
</script>
